import axios from 'axios';

const API_URL =
    (import.meta?.env?.VITE_API_URL || process.env.VUE_APP_API_URL) ||
    'http://localhost:8000/api';

const axiosInstance = axios.create({
    baseURL: API_URL,
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication error:', error.response.data);
        }
        return Promise.reject(error);
    }
);


class MpidApiService {
    async getMeteringPoints(params = {}) {
        try {
            const queryParams = new URLSearchParams();

            if (params.pagination === false) queryParams.append('pagination', 'false');
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.mpid) queryParams.append('mpid', params.mpid);
            if (params.city) queryParams.append('city', params.city);

            const url = `/meteringpoints${queryParams.toString() ? '?' + queryParams : ''}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching metering points:', error);
            throw error;
        }
    }

    // Fetch and normalize a single metering point (and its last active device) for UI
    async getMeteringPoint(mpid) {
        try {
            const { data } = await axiosInstance.get(`/meteringpoints/${mpid}`);
            return this.normalizeMeteringPoint(data);
        } catch (error) {
            console.error(`Error fetching metering point ${mpid}:`, error);
            throw error;
        }
    }

    async deleteMeteringPoint(mpid) {
        try {
            await axiosInstance.delete(`/meteringpoints/${mpid}`);
            // 204 No Content expected
            return true;
        } catch (error) {
            console.error(`Error deleting metering point ${mpid}:`, error);
            throw error;
        }
    }

    async createMeteringPoint(mpidData) {
        try {
            const formattedData = this.formatMeteringPointDataForBackend(mpidData);
            const response = await axiosInstance.post('/meteringpoints', formattedData);
            return response.data;
        } catch (error) {
            console.error('Error creating metering point:', error.response?.data || error);
            throw error;
        }
    }

    async updateMeteringPoint(mpid, updateData) {
        try {
            const response = await axiosInstance.put(`/meteringpoints/${mpid}`, updateData);
            return this.normalizeMeteringPoint(response.data);
        } catch (error) {
            console.error('Error updating metering point:', error.response?.data || error);
            throw error;
        }
    }

    // Geocode an address to get coordinates
    async geocodeAddress(addressData) {
        try {
            const response = await axiosInstance.post('/geocoding', addressData);
            return response.data; // Should return { lon: number, lat: number }
        } catch (error) {
            console.error('Error geocoding address:', error.response?.data || error);
            throw error;
        }
    }

    // ---------- LoRa Methods ----------

    /**
     * Fetch all active LoRa devices with metering point and network diagnostics data
     * @returns {Promise<Array>} Array of LoRa devices with location and session data
     */
    async getLoraActiveMpid(opts = {}) {
        try {
            const params = new URLSearchParams();
            if (opts.refresh) params.append('refresh', 'true');
            if (opts.include) params.append('include', opts.include);
            const url = `/devices/lora/active-mpid${params.toString() ? `?${params}` : ''}`;
            const response = await axiosInstance.get(url);
            return this.normalizeLoraDevices(response.data);
        } catch (error) {
            console.error('Error fetching LoRa active devices:', error);
            throw error;
        }
    }

    /**
     * Normalize LoRa device data for consistent usage
     * @param {Array} devices - Raw LoRa devices from API
     * @returns {Array} Normalized LoRa devices
     */
    normalizeLoraDevices(devices) {
        if (!Array.isArray(devices)) return [];

        return devices.map(device => ({
            // Device identifiers
            tenant_id: device.tenant_id ?? null,
            mpid: device.mpid ?? null,
            logical_device_number: device.logical_device_number ?? null,
            id_radio: device.id_radio ?? null,
            lora_eui: device.lora_eui ?? null,
            customer_number: device.customer_number ?? null,

            // Device configuration
            type: device.type ?? null,
            is_integrated: !!device.is_integrated,
            protocol: device.protocol ?? null,
            caliber: device.caliber ?? null,
            distribution_company: device.distribution_company ?? null,
            installed: device.installed ?? null,

            // Security keys
            lorakey: device.lorakey ?? null,
            lorantwkey: device.lorantwkey ?? null,
            wbuskey: device.wbuskey ?? null,

            // Location data
            street: device.street ?? null,
            city: device.city ?? null,
            zipcode: device.zipcode ?? device.zip_code ?? null,
            lat: device.lat ?? null,
            lon: device.lon ?? null,
            customerfname: device.customerfname ?? device.customer_fname ?? null,

            // Activity tracking
            last_active_mpid: Array.isArray(device.last_active_mpid)
                ? device.last_active_mpid
                : [],

            // Last reading data (if available)
            last_reading: device.last_reading ? {
                logical_device_number: device.last_reading.logical_device_number ?? null,
                current_reading: device.last_reading.current_reading ?? null,
                date_current_reading: device.last_reading.date_current_reading ?? null,
                readed_by: device.last_reading.readed_by ?? null,
                dlms_session_id: device.last_reading.dlms_session_id ?? null,
                created_at: device.last_reading.created_at ?? null,
            } : null,

            // Gateway position only — signal/RF data comes from the loramanager
            // gRPC source (LoraApiService). gateway_id is kept as a fallback hint
            // when loramanager is unavailable.
            last_session: device.last_session ? {
                gateway_id: device.last_session.gateway_id ?? null,
                location_lat: device.last_session.location_lat ?? null,
                location_lon: device.last_session.location_lon ?? null,
                location_alt: device.last_session.location_alt ?? null,
            } : null,
        }));
    }

    // ---------- Helpers ----------
    normalizeMeteringPoint(raw) {
        if (!raw || typeof raw !== 'object') return null;

        const mp = {
            mpid: raw.mpid ?? null,
            city: raw.city ?? null,
            street: raw.street ?? null,
            // accept both zip_code and zipcode in API
            zip_code: raw.zip_code ?? raw.zipcode ?? null,
            zipcode: raw.zipcode ?? raw.zip_code ?? null,
            country: raw.country ?? null,
            lat: raw.lat ?? null,
            lon: raw.lon ?? null,
            // accept both customer_fname and customerfname in API
            customer_fname: raw.customer_fname ?? raw.customerfname ?? null,
            // accept both created_at and createdat in API
            created_at: raw.created_at ?? raw.createdat ?? null,
        };

        // Normalize last active device if present
        const lad = raw.last_active_device ?? raw.lastactivedevice ?? null;
        if (lad && typeof lad === 'object') {
            mp.last_active_device = {
                tenant_id: lad.tenant_id ?? null,
                mpid: lad.mpid ?? mp.mpid ?? null,
                logical_device_number: lad.logical_device_number ?? lad.ldn ?? null,
                customer_number: lad.customer_number ?? null,
                type: lad.type ?? null,
                is_integrated: !!lad.is_integrated,
                protocol: lad.protocol ?? null,
                ctrkey: lad.ctrkey ?? null,
                installed: lad.installed ?? null,
                distribution_company: lad.distribution_company ?? null,
                last_active_mpid: Array.isArray(lad.last_active_mpid) ? lad.last_active_mpid : (lad.last_active_mpid ?? [])
            };
        }

        return mp;
    }

    formatMeteringPointDataForBackend(data) {
        return {
            mpid: data.mpid,
            street: data.street,
            city: data.city,
            zip_code: data.zipCode || null,
            customer_fname: data.customerFName,
            country: data.country || null,
            lat: data.lat !== null && data.lat !== '' ? data.lat : null,
            lon: data.lon !== null && data.lon !== '' ? data.lon : null,
        };
    }
}

export default new MpidApiService();